import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Select } from 'antd';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { formatDate, formatMoneyWithSeparators, formatPercentValue } from '@/utils';
import { DateFormats } from '@/constants/dates';
import { COLORS } from '@/theme';
import { CancelledOrdersGroupBy, ICancelledOrdersData } from '@/models/cancelledOrders.model';
import { aggregateCancelledSeries, formatChartAxisTickWithSpan } from '../../helpers';
import {
  StyledChartColumn,
  StyledChartsCard,
  StyledChartsGrid,
  StyledChartsHeader,
  StyledChartsTitle,
  StyledChartSummary,
  StyledChartTitle,
  StyledChartWrapper,
  StyledSummaryRow,
} from './ChartsBlockStyles';
import InfoLabel from '@/modules/Dashboard/components/InfoLabel';

interface IChartsBlockProps {
  data: ICancelledOrdersData | null;
  groupBy: CancelledOrdersGroupBy;
  onGroupByChange: (value: CancelledOrdersGroupBy) => void;
  pdfExporting?: boolean;
}

const ChartsBlock = ({ data, groupBy, onGroupByChange, pdfExporting = false }: IChartsBlockProps) => {
  const { t, i18n } = useTranslation(['dashboard']);
  const totals = data?.totals;

  const series = useMemo(() => aggregateCancelledSeries(data?.series ?? [], groupBy), [data?.series, groupBy]);

  const tickFormatter = (date: string) => formatChartAxisTickWithSpan(date, groupBy, series, i18n.language);
  const chartMargin = pdfExporting ? { top: 12, right: 8, left: 0, bottom: 8 } : undefined;
  const axisTick = pdfExporting ? { fontSize: 12 } : undefined;
  const axisPadding = pdfExporting ? { left: 0, right: 0 } : undefined;

  return (
    <StyledChartsCard>
      <StyledChartsHeader>
        <StyledChartsTitle>{t('cancelled_orders_charts_title')}</StyledChartsTitle>
        <Select
          className="no-print"
          value={groupBy}
          style={{ minWidth: 140 }}
          onChange={onGroupByChange}
          options={[
            { value: 'day', label: t('cancelled_orders_group_day') },
            { value: 'week', label: t('cancelled_orders_group_week') },
            { value: 'month', label: t('cancelled_orders_group_month') },
          ]}
        />
      </StyledChartsHeader>
      <StyledChartsGrid>
        <StyledChartColumn>
          <StyledChartTitle>
            <InfoLabel
              label={t('cancelled_orders_chart_amount')}
              tooltip={t('cancelled_orders_chart_amount_tooltip')}
            />
          </StyledChartTitle>
          <StyledChartSummary>
            <StyledSummaryRow $color={COLORS.GREEN}>
              {t('cancelled_orders_confirmed')}: {formatMoneyWithSeparators(totals?.confirmed_amount)}{' '}
              {t('dashboard_grn')}
            </StyledSummaryRow>
            <StyledSummaryRow $color={COLORS.RED}>
              {t('cancelled_orders_cancelled')}: {formatMoneyWithSeparators(totals?.cancelled_amount)}{' '}
              {t('dashboard_grn')}
            </StyledSummaryRow>
          </StyledChartSummary>
          <StyledChartWrapper>
            <ResponsiveContainer>
              <LineChart data={series} margin={chartMargin}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickFormatter={tickFormatter}
                  minTickGap={pdfExporting ? 72 : undefined}
                  tick={axisTick}
                  padding={axisPadding}
                />
                <YAxis width={pdfExporting ? 88 : undefined} tick={axisTick} />
                <Tooltip
                  labelFormatter={(label) => formatDate(String(label), DateFormats.EUROPEAN_DEFAULT)}
                  formatter={(value: number, name: string) => [
                    `${formatMoneyWithSeparators(value)} ${t('dashboard_grn')}`,
                    name,
                  ]}
                />
                <Legend wrapperStyle={pdfExporting ? { fontSize: 12 } : undefined} />
                <Line
                  type="monotone"
                  dataKey="confirmed_amount"
                  stroke={COLORS.GREEN}
                  name={t('cancelled_orders_confirmed')}
                  dot={false}
                  isAnimationActive={!pdfExporting}
                />
                <Line
                  type="monotone"
                  dataKey="cancelled_amount"
                  stroke={COLORS.RED}
                  name={t('cancelled_orders_cancelled')}
                  dot={false}
                  isAnimationActive={!pdfExporting}
                />
              </LineChart>
            </ResponsiveContainer>
          </StyledChartWrapper>
        </StyledChartColumn>

        <StyledChartColumn>
          <StyledChartTitle>
            <InfoLabel label={t('cancelled_orders_chart_count')} tooltip={t('cancelled_orders_chart_count_tooltip')} />
          </StyledChartTitle>
          <StyledChartSummary>
            <StyledSummaryRow $color={COLORS.GREEN}>
              {t('cancelled_orders_confirmed')}: {totals?.confirmed_count ?? 0} {t('cancelled_orders_pcs')}
            </StyledSummaryRow>
            <StyledSummaryRow $color={COLORS.RED}>
              {t('cancelled_orders_cancelled')}: {totals?.cancelled_count ?? 0} {t('cancelled_orders_pcs')}
            </StyledSummaryRow>
          </StyledChartSummary>
          <StyledChartWrapper>
            <ResponsiveContainer>
              <LineChart data={series} margin={chartMargin}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickFormatter={tickFormatter}
                  minTickGap={pdfExporting ? 72 : undefined}
                  tick={axisTick}
                  padding={axisPadding}
                />
                <YAxis width={pdfExporting ? 88 : undefined} tick={axisTick} />
                <Tooltip
                  labelFormatter={(label) => formatDate(String(label), DateFormats.EUROPEAN_DEFAULT)}
                  formatter={(value: number, name: string) => [`${value} ${t('cancelled_orders_pcs')}`, name]}
                />
                <Legend wrapperStyle={pdfExporting ? { fontSize: 12 } : undefined} />
                <Line
                  type="monotone"
                  dataKey="confirmed_count"
                  stroke={COLORS.GREEN}
                  name={t('cancelled_orders_confirmed')}
                  dot={false}
                  isAnimationActive={!pdfExporting}
                />
                <Line
                  type="monotone"
                  dataKey="cancelled_count"
                  stroke={COLORS.RED}
                  name={t('cancelled_orders_cancelled')}
                  dot={false}
                  isAnimationActive={!pdfExporting}
                />
              </LineChart>
            </ResponsiveContainer>
          </StyledChartWrapper>
        </StyledChartColumn>

        <StyledChartColumn>
          <StyledChartTitle>
            <InfoLabel
              label={t('cancelled_orders_chart_percent')}
              tooltip={t('cancelled_orders_chart_percent_tooltip')}
            />
          </StyledChartTitle>
          <StyledChartSummary>
            <StyledSummaryRow $color={COLORS.GREEN}>
              {t('cancelled_orders_confirmed')}:{' '}
              {formatPercentValue(totals ? 100 - totals.cancelled_share_percent : 0, 1, i18n.language)}
            </StyledSummaryRow>
            <StyledSummaryRow $color={COLORS.RED}>
              {t('cancelled_orders_cancelled')}:{' '}
              {formatPercentValue(totals?.cancelled_share_percent ?? 0, 1, i18n.language)}
            </StyledSummaryRow>
          </StyledChartSummary>
          <StyledChartWrapper>
            <ResponsiveContainer>
              <LineChart data={series} margin={chartMargin}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickFormatter={tickFormatter}
                  minTickGap={pdfExporting ? 72 : undefined}
                  tick={axisTick}
                  padding={axisPadding}
                />
                <YAxis domain={[0, 100]} width={pdfExporting ? 88 : undefined} tick={axisTick} />
                <Tooltip
                  labelFormatter={(label) => formatDate(String(label), DateFormats.EUROPEAN_DEFAULT)}
                  formatter={(value: number, name: string) => [
                    formatPercentValue(Number(value), 1, i18n.language),
                    name,
                  ]}
                />
                <Legend wrapperStyle={pdfExporting ? { fontSize: 12 } : undefined} />
                <Line
                  type="monotone"
                  dataKey="confirmed_share_percent"
                  stroke={COLORS.GREEN}
                  name={t('cancelled_orders_confirmed')}
                  dot={false}
                  isAnimationActive={!pdfExporting}
                />
                <Line
                  type="monotone"
                  dataKey="cancelled_share_percent"
                  stroke={COLORS.RED}
                  name={t('cancelled_orders_cancelled')}
                  dot={false}
                  isAnimationActive={!pdfExporting}
                />
              </LineChart>
            </ResponsiveContainer>
          </StyledChartWrapper>
        </StyledChartColumn>
      </StyledChartsGrid>
    </StyledChartsCard>
  );
};

export default ChartsBlock;
